// Where a place lives in the web (CR_Manager_vs_User_role P14): a warehouse under /warehouses, a project location
// inside its project (the location picked with ?location=) or, without a project, under /locations. Every link to a
// place goes through here.
import { placeKind, type SiteType } from '@fleetspoke/core/places';

export const PLACE_TABS = ['overview', 'assets', 'map', 'counts', 'movements'] as const;
export type PlaceTab = (typeof PLACE_TABS)[number];
/** a project page has the place tabs plus Billing (managers only) */
export const PROJECT_TABS = [...PLACE_TABS, 'billing'] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

export interface PlaceRef {
  site_id: string;
  type: SiteType;
  project_id: string | null;
}

/** The page of a place, optionally on one tab; extra query parameters (e.g. ?zone=) are kept. */
export function placePath(site: PlaceRef, tab?: PlaceTab | null, extra?: URLSearchParams): string {
  const params = new URLSearchParams(extra);
  if (tab && tab !== 'overview') params.set('tab', tab);
  else params.delete('tab');
  let base: string;
  if (placeKind(site.type) === 'warehouse') base = `/warehouses/${site.site_id}`;
  else if (site.project_id) {
    base = `/projects/${site.project_id}`;
    params.set('location', site.site_id);
  } else base = `/locations/${site.site_id}`;
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * The place a transfer belongs to for the viewer (CR_20260915_Request_Qty R0): its destination when they can see it,
 * else its origin. The menu highlight and the transfer page's back link follow it.
 */
export function transferHome<T extends { site_id: string }>(
  transfer: { from_site_id: string | null; to_site_id: string | null },
  sites: readonly T[],
): T | null {
  return (
    sites.find((s) => s.site_id === transfer.to_site_id) ??
    sites.find((s) => s.site_id === transfer.from_site_id) ??
    null
  );
}

/** ?tab= as a known tab, else the first one. */
export function placeTab(value: string | null): PlaceTab {
  return (PLACE_TABS as readonly string[]).includes(value ?? '') ? (value as PlaceTab) : 'overview';
}
